import { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  MenuItem,
  Stack,
  ToggleButtonGroup,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  ToggleButton as MuiToggleButton,
} from "@mui/material";
// import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { Row, Label } from "../index.styles";
import { DecimalInput, Select, ToggleButton } from "../../../components";

import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  selectProductAnalyzerSettings,
  selectGeneralSettings,
} from "../../../features/settings/settings.selector";
import {
  updateProductAnalyzerSettingsThunk,
  updateGeneralSettingsThunk,
} from "../../../features/settings/settings.thunk";
import {
  MAX_BSR_VALIDATION_ERROR,
  MIN_BSR_VALIDATION_ERROR,
} from "../../../constants";
import { TInboundPlacementService } from "../../../features/feeSettings/feeSettingsSlice";
import { TFulfilmentType } from "../../BulkAnalyzer/FulfilmentDropdown";
import { INBOUND_PLACEMENT_TYPES } from "../../../constants/productAnalyzer";

type TErrors = {
  minBsr: string | null;
  maxBsr: string | null;
};

const SourcingSettings = () => {
  const dispatch = useAppDispatch();
  const productAnalyzerSettings = useAppSelector(selectProductAnalyzerSettings);
  const generalSettings = useAppSelector(selectGeneralSettings);
  const [errors, setErrors] = useState<TErrors>({ minBsr: null, maxBsr: null });
  const [minBsr, setMinBsr] = useState<number>(
    productAnalyzerSettings?.minBsr || 0,
  );
  const [maxBsr, setMaxBsr] = useState<number>(
    productAnalyzerSettings?.maxBsr || 0,
  );
  const [isSourcingCriteriaOpen, setIsSourcingCriteriaOpen] = useState(true);
  const [isAdditionalExpensesOpen, setIsAdditionalExpensesOpen] =
    useState(true);

  useEffect(() => {
    if (productAnalyzerSettings && minBsr > maxBsr) {
      setErrors({
        minBsr: MIN_BSR_VALIDATION_ERROR,
        maxBsr: MAX_BSR_VALIDATION_ERROR,
      });
    } else {
      setErrors({
        minBsr: null,
        maxBsr: null,
      });
    }
  }, [minBsr, maxBsr, productAnalyzerSettings]);

  if (!productAnalyzerSettings || !generalSettings) return null;

  return (
    <Grid
      container
      spacing="16px"
      justifyContent="center"
      sx={{
        overflowX: "visible",
        flexWrap: "wrap",
        "@media (max-width:319px)": {
          overflowX: "auto",
          flexWrap: "nowrap",
          justifyContent: "flex-start",
        },
      }}
    >
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{
          minWidth: "auto",
          maxWidth:"560px",
          "@media (max-width:319px)": {
            minWidth: "320px",
          },
        }}
      >
        <Stack spacing={1}>
          <Card
            sx={{
              boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.1)",
              borderRadius: "4px",
              border: "1px solid #D4D6D8",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 8px",
                minHeight: "30px !important",
                cursor: "pointer",
                height: "30px",
                backgroundColor: "#F0F0F0",
                borderBottom: "1px solid #C9C9C9",
                borderRadius: "4px 4px 0px 0px",
              }}
              onClick={() => setIsSourcingCriteriaOpen(!isSourcingCriteriaOpen)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* <Box
                  sx={{
                    width: 25,
                    height: 25,
                    borderRadius: "14px",
                    backgroundColor: "#3366FF1A",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TrendingUpIcon
                    sx={{
                      fontSize: 16,
                      color: "#0F56CA",
                    }}
                  />
                </Box> */}
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}
                >
                  Sourcing Criteria
                </Typography>
              </Box>
              <IconButton
                size="small"
                sx={{ padding: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSourcingCriteriaOpen(!isSourcingCriteriaOpen);
                }}
              >
                {isSourcingCriteriaOpen ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </Box>
            <Collapse in={isSourcingCriteriaOpen}>
              <CardContent
                sx={{
                  padding: 0,
                  "&:last-child": { paddingBottom: "0px" },
                }}
              >
                <Stack spacing={0}>
                  <Row>
                    <Label
                      error={!!errors["minBsr"]}
                      tooltip="The lowest sales rank you want to source."
                    >
                      Minimum BSR(%)
                    </Label>
                    <DecimalInput
                      suffix="%"
                      value={productAnalyzerSettings.minBsr}
                      onChange={(val: number) => {
                        if (val <= productAnalyzerSettings.maxBsr) {
                          dispatch(
                            updateProductAnalyzerSettingsThunk({
                              minBsr: val,
                              maxBsr,
                            }),
                          );
                        }
                      }}
                      onEditing={setMinBsr}
                      align="left"
                      min={1}
                      max={100}
                      step={1}
                      error={!!errors["minBsr"]}
                      helperText={errors["minBsr"] || ""}
                    />
                  </Row>
                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />
                  <Row>
                    <Label
                      error={!!errors["maxBsr"]}
                      tooltip="The highest sales rank you are willing to accept."
                    >
                      Maximum BSR(%)
                    </Label>
                    <DecimalInput
                      suffix="%"
                      value={productAnalyzerSettings.maxBsr}
                      onChange={(val: number) => {
                        if (val >= productAnalyzerSettings.minBsr) {
                          dispatch(
                            updateProductAnalyzerSettingsThunk({
                              minBsr,
                              maxBsr: val,
                            }),
                          );
                        }
                      }}
                      onEditing={setMaxBsr}
                      align="left"
                      min={1}
                      max={100}
                      step={1}
                      error={!!errors["maxBsr"]}
                      helperText={errors["maxBsr"] || ""}
                    />
                  </Row>
                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />
                  <Row>
                    <Label tooltip="Minimum estimated profit per unit after fees and expenses.">
                      Minimum Profit
                    </Label>
                    <DecimalInput
                      prefix="$"
                      value={productAnalyzerSettings.minProfit}
                      onChange={(minProfit: number) => {
                        dispatch(
                          updateProductAnalyzerSettingsThunk({ minProfit }),
                        );
                      }}
                      align="right"
                    />
                  </Row>
                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />
                  <Row>
                    <Label tooltip="Minimum return on investment required for a product to qualify.">
                      Minimum ROI
                    </Label>
                    <DecimalInput
                      suffix="%"
                      value={productAnalyzerSettings.minRoi}
                      onChange={(minRoi: number) => {
                        dispatch(
                          updateProductAnalyzerSettingsThunk({ minRoi }),
                        );
                      }}
                      align="left"
                    />
                  </Row>
                  <Divider sx={{ borderColor: "#E5E7EB", mt: 0 }} />
                  <Box
                    sx={{
                      py: "4px",
                      px: "4px",
                      fontSize: "11px",
                      color: "#565D6DCC",
                      lineHeight: "18px",
                      fontWeight: 400,
                      textAlign: "center",
                    }}
                  >
                    Customize your sourcing benchmarks to automatically score
                    opportunities against your profitability and demand
                    requirements.
                  </Box>
                </Stack>
              </CardContent>
            </Collapse>
          </Card>

          <Card
            sx={{
              boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.1)",
              borderRadius: "4px",
              border: "1px solid #D4D6D8",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 8px",
                minHeight: "30px !important",
                cursor: "pointer",
                height: "30px",
                backgroundColor: "#F0F0F0",
                borderBottom: "1px solid #C9C9C9",
                borderRadius: "4px 4px 0px 0px",
              }}
              onClick={() =>
                setIsAdditionalExpensesOpen(!isAdditionalExpensesOpen)
              }
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* <Box
                  sx={{
                    width: 25,
                    height: 25,
                    borderRadius: "14px",
                    backgroundColor: "#3366FF1A",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TrendingUpIcon
                    sx={{
                      fontSize: 16,
                      color: "#0F56CA",
                    }}
                  />
                </Box> */}
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}
                >
                  Additional Expenses
                </Typography>
              </Box>
              <IconButton
                size="small"
                sx={{ padding: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAdditionalExpensesOpen(!isAdditionalExpensesOpen);
                }}
              >
                {isAdditionalExpensesOpen ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </Box>
            <Collapse in={isAdditionalExpensesOpen}>
              <CardContent
                sx={{
                  padding: 0,
                  "&:last-child": { paddingBottom: "0px" },
                }}
              >
                <Stack spacing={0}>
                  <Row>
                    <Label tooltip="Estimated cost to ship inventory to Amazon fulfillment centers.">
                      Inbound FBA Shipping
                    </Label>
                    <DecimalInput
                      prefix="$"
                      value={generalSettings.inboundShipping}
                      onChange={(inboundShipping: number) =>
                        dispatch(
                          updateGeneralSettingsThunk({ inboundShipping }),
                        )
                      }
                      align="right"
                    />
                  </Row>
                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />
                  <Row>
                    <Label tooltip="Select how Amazon distributes inventory across warehouses.">
                      Inbound Placement
                    </Label>
                    <Select
                      value={generalSettings.inboundPlacement}
                      onChange={(e) =>
                        dispatch(
                          updateGeneralSettingsThunk({
                            inboundPlacement: e.target
                              .value as TInboundPlacementService,
                          }),
                        )
                      }
                      size="medium"
                      fullWidth
                    >
                      {INBOUND_PLACEMENT_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type === "Optimized"
                            ? "Amazon Optimized Split"
                            : type === "Partial"
                              ? "Partial Shipment Splits"
                              : "Minimal Shipment Splits"}
                        </MenuItem>
                      ))}
                    </Select>
                  </Row>
                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />
                  <Row>
                    <Label tooltip="Expected months inventory will be stored before selling.">
                      Storage Time
                    </Label>
                    <DecimalInput
                      suffix="months"
                      value={generalSettings.storageTime}
                      onChange={(storageTime: number) =>
                        dispatch(updateGeneralSettingsThunk({ storageTime }))
                      }
                      align="left"
                    />
                  </Row>
                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />
                  <Row>
                    <Label tooltip="Choose whether profit calculations use FBA or merchant fulfillment fees.">
                      Fulfillment Type
                    </Label>
                    <ToggleButtonGroup
                      value={generalSettings.fulfilment}
                      onChange={(_e, value) => {
                        // Prevent deselection - if clicking the same value, do nothing
                        if (value === generalSettings.fulfilment || !value) {
                          return;
                        }
                        dispatch(
                          updateGeneralSettingsThunk({
                            fulfilment: value as TFulfilmentType,
                          }),
                        );
                      }}
                      exclusive
                      sx={{
                        borderRadius: "4px",
                        backgroundColor: "#FFFFFF",
                        "& .MuiToggleButton-root": {
                          border: "none",
                          borderRadius: "4px",
                          textTransform: "none",
                          fontSize: "12px",
                          fontWeight: 500,
                          lineHeight: "22px",
                          minWidth: "80px",
                          padding: "0",
                          color: "#323842",
                          height: "34px",
                          backgroundColor: "transparent",
                          "&.Mui-selected": {
                            backgroundColor: "#EBF6FF",
                            color: "#0F56CA",
                            border: "1px solid #0F56CA",
                            "&:hover": {
                              backgroundColor: "#EBF6FF",
                            },
                          },
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        },
                      }}
                    >
                      <MuiToggleButton value="FBA">FBA</MuiToggleButton>
                      <MuiToggleButton value="FBM">FBM</MuiToggleButton>
                    </ToggleButtonGroup>
                  </Row>
                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />
                  <Row>
                    <Label tooltip="Your cost to fulfill FBM orders to be included in the calculations. Can be manually adjusted as you analyze.">
                      FBM Cost
                    </Label>
                    <DecimalInput
                      prefix="$"
                      value={productAnalyzerSettings.defaultFbmCost}
                      onChange={(defaultFbmCost: number) => {
                        dispatch(
                          updateProductAnalyzerSettingsThunk({
                            defaultFbmCost,
                          }),
                        );
                      }}
                      align="right"
                    />
                  </Row>
                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />
                  <Row>
                    <Label tooltip="Cost per unit for labeling, packaging, or preparation.">
                      Prep Fee
                    </Label>
                    <DecimalInput
                      prefix="$"
                      value={generalSettings.prepFee}
                      onChange={(prepFee: number) =>
                        dispatch(updateGeneralSettingsThunk({ prepFee }))
                      }
                      align="right"
                    />
                  </Row>
                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />
                  <Row>
                    <Label tooltip="Additional fixed costs per unit not included elsewhere.">
                      Misc Fee
                    </Label>
                    <DecimalInput
                      prefix="$"
                      value={generalSettings.miscFee}
                      onChange={(miscFee: number) =>
                        dispatch(updateGeneralSettingsThunk({ miscFee }))
                      }
                      align="right"
                    />
                  </Row>
                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />
                  <Row>
                    <Label tooltip="Extra expenses calculated as a percentage of product cost.">
                      Misc Fee (% of cost)
                    </Label>
                    <DecimalInput
                      suffix="%"
                      value={generalSettings.miscFeePercent}
                      onChange={(miscFeePercent: number) =>
                        dispatch(updateGeneralSettingsThunk({ miscFeePercent }))
                      }
                      align="left"
                    />
                  </Row>
                </Stack>
              </CardContent>
            </Collapse>
          </Card>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default SourcingSettings;
